import {
  Bell,
  Building2,
  ClipboardList,
  CreditCard,
  FolderOpen,
  Mail,
  PanelTop,
  Package,
  Plug,
  Users,
  type LucideIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { BetaBadge } from '@/components/shared/beta-badge'

/**
 * Everything a Settings destination's `visible` predicate is allowed to consult.
 * Mirrors what the sidebar already computes for its Settings group, so the
 * sidebar and the `/settings` index can never disagree about who sees what.
 */
export interface SettingsNavContext {
  /** Permission check for the currently selected organization. */
  can: (permission: string) => boolean
}

export interface SettingsNavItem {
  title: string
  url: string
  icon: LucideIcon
  /** One-sentence summary rendered on the `/settings` index card. */
  description: string
  badge?: ReactNode
  /** Omitted means member-level: always visible. */
  visible?: (context: SettingsNavContext) => boolean
}

/**
 * The single ordered definition of the Settings section, consumed by both the
 * sidebar's Settings group and the `/settings` index grid.
 *
 * Each entry is gated on the permission its destination's *primary* read
 * requires per the backend policy registry
 * (backend/internal/api/middleware/rbacpolicy/policy_definitions.go), so an
 * item only appears when the page behind it can actually load. Pages whose
 * primary read is member-level (Attachment Library) carry no gate at all.
 * Destinations with two independent entry permissions OR them, as Company
 * already did.
 */
export const settingsNavItems: SettingsNavItem[] = [
  {
    title: 'Company',
    url: '/company',
    icon: Building2,
    description: 'Profile, time settings, lead assignment, and subscription',
    visible: ({ can }) => can('SettingsManage') || can('BillingManage'),
  },
  {
    title: 'Team & Roles',
    url: '/team',
    icon: Users,
    description: 'Team members and the roles that grant their permissions',
    visible: ({ can }) => can('TeamManage'),
  },
  {
    title: 'Services & Pricing',
    url: '/services',
    icon: Package,
    description: 'Catalog, inventory, event types, quote templates, discounts, tax, and gratuity',
    visible: ({ can }) => can('CatalogView'),
  },
  {
    title: 'Email',
    url: '/email',
    icon: Mail,
    description: 'Senders, domain and inbox, templates, deliverability, and health',
    badge: <BetaBadge />,
    // Templates and deliverability are Comms; senders, domain and health are
    // Settings — either one makes the page worth showing.
    visible: ({ can }) => can('CommsView') || can('SettingsManage'),
  },
  {
    title: 'Attachment Library',
    url: '/attachment-library',
    icon: FolderOpen,
    description: 'Reusable files and folders to attach to emails',
  },
  {
    title: 'Inquiry Form',
    url: '/forms',
    icon: ClipboardList,
    description: 'Publish, fields, availability, theme, confirmation, and analytics',
    visible: ({ can }) => can('SettingsManage'),
  },
  {
    title: 'Client Portal',
    url: '/client-portal',
    icon: PanelTop,
    description: 'Portal domain, terms and conditions, and customer tipping',
    visible: ({ can }) => can('SettingsManage'),
  },
  {
    title: 'Notifications',
    url: '/notifications',
    icon: Bell,
    description: 'Scheduled digest email for your team',
    visible: ({ can }) => can('SettingsManage'),
  },
  {
    title: 'Payments',
    url: '/payments',
    icon: CreditCard,
    // Stripe Connect and webhooks are Settings; disputes are Payments.
    description: 'Stripe account, disputes, and webhooks',
    visible: ({ can }) => can('SettingsManage') || can('PaymentsView'),
  },
  {
    title: 'Integrations',
    url: '/integrations',
    icon: Plug,
    description: 'Connected apps, Google Calendar, and webhooks',
    visible: ({ can }) => can('SettingsManage') || can('AutomationsView'),
  },
]

/** The Settings destinations this user may actually reach, in section order. */
export function getVisibleSettingsNavItems(context: SettingsNavContext): SettingsNavItem[] {
  return settingsNavItems.filter((item) => item.visible?.(context) ?? true)
}
